import React, { useEffect } from 'react';
import _ from 'lodash';
import { ChartView } from '@/trend/ChartView.organism';
import { ChartViewActions, ChartViewConfiguration, ChartViewData } from '@/trend/chart.types';
import { DisplayRangeSelector } from '@/trend/DisplayRangeSelector.molecule';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { getScaledFontSize } from '@/core/html.utilities';
import { CAPSULE_LABEL_HEIGHT_REM } from '@/utilities/chartLanes.constants';
import { EmptyContent } from '@/reportEditor/EmptyContent.molecule';

export interface ChartViewContentProperties {
  onContentLoad: () => void;
  afterChartUpdate: () => void;
  data: ChartViewData;
  configuration: ChartViewConfiguration;
  actions: ChartViewActions;
  darkMode?: boolean;
  onHighchartsLoad: () => void;
}

/**
 * This component is for rendering interactive content in trend view
 */
export const ChartViewContent: React.FunctionComponent<ChartViewContentProperties> = (props) => {
  const { onContentLoad, afterChartUpdate, darkMode = false, onHighchartsLoad } = props;

  useEffect(() => onContentLoad(), []);
  props.actions.onContentUpdate = onHighchartsLoad;
  props.configuration.darkMode = darkMode;
  props.actions.afterChartUpdate = afterChartUpdate;

  // The lineWidth for capsules is computed when captured, not when rendered. So this uses the same formula to scale
  // the lineWidth based on font size when rendering interactive content
  if (!props.data.sqTrendStoreData.canCapsulesExpand) {
    const headlessCapsuleHeight = getScaledFontSize(CAPSULE_LABEL_HEIGHT_REM, true);
    const scaledFontSize = getScaledFontSize(CAPSULE_LABEL_HEIGHT_REM);
    props.data.items
      .filter(
        (item) =>
          !Object.isFrozen(item) && item.itemType === ITEM_TYPES.CAPSULE && item.dataLabels?.enabled && item.lineWidth,
      )
      .forEach((item) => {
        item.lineWidth = (item.lineWidth / headlessCapsuleHeight) * scaledFontSize;
      });
  }

  return !props.data.items.length ? (
    <EmptyContent onContentLoad={_.noop} />
  ) : (
    <>
      <ChartView actions={props.actions} configuration={props.configuration} data={props.data} />
      <DisplayRangeSelector
        displayRange={props.data.sqDurationStoreData.displayRange}
        rangeEditingEnabled={false}
        autoUpdateDisabled={true}
        label={props.data.summaryLabel}
        timezone={props.data.selectedTimezone}
        investigateRangeCopyDisabled={true}
        isInTopic={true}
      />
    </>
  );
};
